import { useRouter as useNextRouter } from "next/router";
import { isEmpty, isString } from "lodash";
import type { UrlObject } from "url";
import useStore from "@common/state";
import {
  ClientRoutes,
  CareerStage,
  CareerStageOptions,
  themeBreakpoints,
  HEADER_TITLE_BY_PATH_COBRANDED,
  HEADER_TITLE_BY_PATH,
  FeatureFlags,
} from "@common/constants";
import { useHousehold, useProfile, useProfileUpdate } from "@common/queries";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { resetPersistedStores } from "./utils";
import { useWindowSize } from "usehooks-ts";
import useAdvisorStore from "@modules/advisor/state";
import { useUser } from "@auth0/nextjs-auth0/client";
import { useSignupInfo } from "@modules/signup/queries";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AboutUsReferral } from "@earned/wizard";
import startCase from "lodash/startCase";

interface TransitionOptions {
  shallow?: boolean;
  locale?: string | false;
  scroll?: boolean;
}
type Url = UrlObject | string;

const useRouter = () => {
  const router = useNextRouter();
  const { setDesiredPath } = useStore();
  return {
    ...router,
    push: (url: Url, as?: Url, options?: TransitionOptions) => {
      const promise = router.push(url, as, options);
      if (isString(url)) {
        setDesiredPath(url as string);
      }
      return promise;
    },
  };
};

const useLogout = () => {
  const {
    setIsLoggingOut,
    showLogoutDialog,
    setShowLogoutDialog,
    isLoggingOut,
  } = useStore();
  const router = useNextRouter();
  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    await resetPersistedStores();
    window.heap?.resetIdentity();
    window.heap?.clearEventProperties();
    router.push(ClientRoutes.Logout);
  }, [router, setIsLoggingOut]);
  return {
    showLogoutDialog,
    isLoggingOut,
    setShowLogoutDialog,
    logout,
  };
};

const useBreakpoints = () => {
  const { width } = useWindowSize();
  return {
    isTablet: Boolean(
      width && width < themeBreakpoints.lg && width >= themeBreakpoints.md
    ),
    isDesktop: Boolean(width && width >= themeBreakpoints.lg),
    isMobile: Boolean(width && width < themeBreakpoints.sm),
  };
};

const useCareerStage = () => {
  const { mutateAsync: updateProfile } = useProfileUpdate();
  const {
    isLoading: isLoadingProfile,
    data: profile,
    refetch: refetchProfile,
  } = useProfile();

  const careerStageValue = useMemo(
    () =>
      profile?.careerStage
        ? {
            id: profile?.careerStage as CareerStage,
            value: profile?.careerStage as CareerStage,
            label:
              CareerStageOptions.find((o) => o.id === profile?.careerStage)
                ?.name ?? "",
            name:
              CareerStageOptions.find((o) => o.id === profile?.careerStage)
                ?.name ?? "",
          }
        : CareerStageOptions[0],
    [profile]
  );

  return {
    value: careerStageValue,
    options: CareerStageOptions,
    isLoading: isLoadingProfile,
    refetch: refetchProfile,
    mutateAsync: updateProfile,
  };
};

const useRouterQueryParam = (query = "ref") => {
  const router = useRouter();

  const queryParam = useMemo(() => {
    return router.query[query];
  }, [router.query, query]);

  const getQueryParamAsURLSearchParams = useCallback(
    (options: { replaceParams: boolean } = { replaceParams: false }) => {
      const { pathname, query: routerQuery } = router;
      return new URLSearchParams(
        options.replaceParams
          ? { [query]: pathname }
          : ({
              ...routerQuery,
              [query]: pathname,
            } as Record<string, string>)
      );
    },
    [router, query]
  );

  const setQueryParam = useCallback(async () => {
    const { pathname } = router;
    const params = getQueryParamAsURLSearchParams();
    await router.replace({ pathname, query: params.toString() }, undefined, {
      shallow: true,
    });
  }, [router, getQueryParamAsURLSearchParams]);

  const removeQueryParam = useCallback(async () => {
    const { pathname, query: routerQuery } = router;
    const params = new URLSearchParams(routerQuery as Record<string, string>);
    params.delete(query);
    await router.replace({ pathname, query: params.toString() }, undefined, {
      shallow: true,
    });
  }, [router, query]);

  return {
    queryParam,
    removeQueryParam,
    setQueryParam,
    getQueryParamAsURLSearchParams,
  };
};

const useIsAdvisor = () => {
  const { impersonateClientUserId } = useAdvisorStore();
  const { user } = useUser();
  const isAdvisor = useMemo(
    () => user && user["https://formefinancial.com/advisor"] === true,
    [user]
  );
  const isAdvisorPortal = useMemo(
    () => isAdvisor && !impersonateClientUserId,
    [isAdvisor, impersonateClientUserId]
  );
  return { isAdvisor, isAdvisorPortal };
};

const useIsAdvisorAdmin = () => {
  const { user } = useUser();
  return useMemo(
    () => user?.["https://formefinancial.com/advisor-admin"] === true,
    [user]
  );
};

// ?viewAs=<advisor email> carries an advisor admin's impersonation across navigation.
// Page state alone dies on a route change, so the sidebar and every link have to read it
// from the URL or the destination silently falls back to the caller's own book.
//
// Non-admins always get undefined. That's a UI guard, not the security boundary — the
// backend ignores advisor_email on any token without the admin claim.
const useViewAsAdvisor = () => {
  // useNextRouter, not the local wrapper: that one overrides push to track
  // desiredPath, which has nothing to do with a shallow query-param replace.
  const router = useNextRouter();
  const isAdvisorAdmin = useIsAdvisorAdmin();

  const activeEmail = useMemo(() => {
    const raw = (router.query.viewAs as string) || "";
    return isAdvisorAdmin ? raw.trim().toLowerCase() : "";
  }, [router.query.viewAs, isAdvisorAdmin]);

  const setViewAs = useCallback(
    (email: string) => {
      const trimmed = email.trim().toLowerCase();
      router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, viewAs: trimmed },
        },
        undefined,
        { shallow: true }
      );
    },
    [router]
  );

  const exitViewAs = useCallback(() => {
    // Drop the param rather than blanking it, so the URL doesn't keep a bare ?viewAs=
    const query = { ...router.query };
    delete query.viewAs;
    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });
  }, [router]);

  return {
    isAdvisorAdmin,
    activeEmail,
    viewAs: activeEmail || undefined,
    setViewAs,
    exitViewAs,
  };
};

const useNextEnvironment = () => {
  const isProduction = process.env.NEXT_PUBLIC_VERCEL_ENV === "production";
  return {
    isProduction,
  };
};

const useFeatureFlag = () => {
  const { isProduction } = useNextEnvironment();
  const isFeatureFlagged = !isProduction;
  return {
    isFeatureFlagged,
  };
};

const usePrev = <V>(value: V) => {
  const ref = useRef<V | undefined>(undefined);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

const usePoFExperience = () => {
  const { user } = useUser();
  const { signupInfo, isLoading, isError, isIdle, isFetched } = useSignupInfo({
    options: { enabled: !!user },
  });
  const { isPoFExperience, setIsPoFExperience } = useStore();

  const isPoFFromSignupInfo =
    signupInfo && signupInfo.ABOUT_US_REFERRAL === "PHYSICIAN_ON_FIRE";

  startTransition(() => {
    if (isPoFFromSignupInfo !== isPoFExperience) {
      setIsPoFExperience(isPoFFromSignupInfo);
    }
  });

  if (!user) {
    return { isPof: false, isWaiting: false };
  }

  return {
    isPof: isPoFExperience || isPoFFromSignupInfo,
    isWaiting:
      isPoFFromSignupInfo === undefined &&
      (isIdle || (isLoading && !isError && !isFetched)),
  };
};

const useTaxClientPortal = () => {
  const flags = useFlags();
  const isTaxClientPortalEnabled = flags[FeatureFlags.TAX_CLIENT_PORTAL];

  return { isTaxClientPortalEnabled };
};

const useUMPExperience = () => {
  const flags = useFlags();
  const isUMPExperienceEnabled = flags[FeatureFlags.UMP_EXPERIENCE];

  const { user } = useUser();
  const { signupInfo, isLoading } = useSignupInfo({
    options: { enabled: !!user },
  });

  const isUMPExperience = isEmpty(signupInfo)
    ? undefined
    : signupInfo.ABOUT_US_REFERRAL === AboutUsReferral.UMP;

  return {
    isUMPExperienceEnabled,
    isUMPExperience,
    isLoading,
  };
};

const useJMMGExperience = () => {
  const flags = useFlags();
  const isJMMGExperienceEnabled = flags[FeatureFlags.JMMG_EXPERIENCE];

  const { user } = useUser();
  const { signupInfo, isLoading } = useSignupInfo({
    options: { enabled: !!user },
  });

  const isJMMGExperience = isEmpty(signupInfo)
    ? undefined
    : signupInfo.ABOUT_US_REFERRAL === AboutUsReferral.JMMG;

  return {
    isJMMGExperienceEnabled,
    isJMMGExperience,
    isLoading,
  };
};

const useECMAExperience = () => {
  const flags = useFlags();
  const isECMAExperienceEnabled = flags[FeatureFlags.ECMA_EXPERIENCE];

  const { user } = useUser();
  const { signupInfo, isLoading } = useSignupInfo({
    options: { enabled: !!user },
  });

  const isECMAExperience = isEmpty(signupInfo)
    ? undefined
    : signupInfo.ABOUT_US_REFERRAL === AboutUsReferral.ECMA;
  return { isECMAExperienceEnabled, isECMAExperience, isLoading };
};

const useSJMGExperience = () => {
  const flags = useFlags();
  const isSJMGExperienceEnabled = flags[FeatureFlags.SJMG_EXPERIENCE];
  const { user } = useUser();
  const { signupInfo, isLoading } = useSignupInfo({
    options: { enabled: !!user },
  });

  const isSJMGExperience = isEmpty(signupInfo)
    ? undefined
    : signupInfo.ABOUT_US_REFERRAL === AboutUsReferral.SJMG;
  return { isSJMGExperienceEnabled, isSJMGExperience, isLoading };
};

const useHeaderTitle = () => {
  const router = useRouter();
  const { pathname } = router;

  const { isCobranded } = useIsCobranded();
  const fallbackTitle = useCallback(
    () => startCase(pathname.split("/")[1] || ""),
    [pathname]
  );

  const path = pathname as keyof typeof HEADER_TITLE_BY_PATH;

  if (isCobranded) {
    return HEADER_TITLE_BY_PATH_COBRANDED[path] ?? fallbackTitle();
  } else {
    return HEADER_TITLE_BY_PATH[path] ?? fallbackTitle();
  }
};

const useMGPWizardEnabled = () => {
  const flags = useFlags();
  return flags[FeatureFlags.MGP_WIZARD];
};

const useMGPWizardTask = () => {
  const flags = useFlags();
  return flags[FeatureFlags.MGP_WIZARD_ADVISOR];
};

const useIsCobranded = () => {
  const { isPof } = usePoFExperience();
  const { isUMPExperience } = useUMPExperience();
  const { isJMMGExperience } = useJMMGExperience();
  const { isECMAExperience } = useECMAExperience();
  const { isSJMGExperience } = useSJMGExperience();
  const { data: household } = useHousehold();

  const isCobranded =
    isPof ||
    isUMPExperience ||
    isJMMGExperience ||
    isECMAExperience ||
    isSJMGExperience;
  const isCobrandedClient = isCobranded && !!household?.orionHouseholdId;

  return { isCobranded, isCobrandedClient };
};

const useTasksManagerEnabled = () => {
  const flags = useFlags();
  const isTasksManagerEnabled = flags[FeatureFlags.TASKS_MANAGER];

  return {
    isTasksManagerEnabled,
  };
};

const useAtlasExperience = () => {
  const flags = useFlags();
  const isAtlasExperienceEnabled = flags[FeatureFlags.ATLAS_EXPERIENCE];

  return { isAtlasExperienceEnabled };
};

const useAtlasDemoMode = () => {
  const flags = useFlags();
  const isAtlasDemoModeEnabled = flags[FeatureFlags.ATLAS_DEMO_MODE];

  return { isAtlasDemoModeEnabled };
};

export {
  useIsAdvisor,
  useIsAdvisorAdmin,
  useViewAsAdvisor,
  useTaxClientPortal,
  useUMPExperience,
  useJMMGExperience,
  useBreakpoints,
  useCareerStage,
  useLogout,
  useRouter,
  useRouterQueryParam,
  useNextEnvironment,
  useFeatureFlag,
  usePrev,
  usePoFExperience,
  useHeaderTitle,
  useMGPWizardEnabled,
  useMGPWizardTask,
  useIsCobranded,
  useTasksManagerEnabled,
  useECMAExperience,
  useSJMGExperience,
  useAtlasExperience,
  useAtlasDemoMode,
};
